import { useEffect, useRef, useState } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { BoundAutocomplete, BoundTextField } from "lib/@components/binding/bound-components"
import { AuthoringBoundTextEditor } from "authoring/components/authoring-editor"
import { EditRecord } from "lib/@components/edit-record"
import { useRefresh } from "lib/@hooks/useRefresh"
import { If } from "lib/@components/switch"
import { getMaintenanceTask, putMaintenanceTask } from "../../controller/maintenance-library"
import { VALID_CLASSIFICATIONS } from "library/SFG20/constants"
import { Frequency } from "routes/schedule/tasks/frequency"
import Iconify from "minimals-template/components/Iconify"
import useToggle from "minimals-template/components/@hooks/useToggle"
import { Repeat } from "lib/@components/repeat"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { AuthoringHeaderBarRight } from "authoring/routes/@layouts/authoring-app-layout"

const TASK_TYPES = ["operational", "maintenance", "specialist"]
const SKILL_SETS = [
    "Competent Person",
    "Competent Person (Fire)",
    "Electrical",
    "Gas Safe",
    "Liquid Fuel Engineer",
    "Refrigeration Engineer",
    "M&E",
    "Accountable Person",
    "User",
    "Specialist",
]
const SAVE_INDICATOR_ID = "maintenance-task"

export function TaskEditor() {
    const { selectedTaskId, setSelectedTaskId, category, refresh: parentRefresh } = useBoundContext()
    const task = getMaintenanceTask.useResultsOnce(
        selectedTaskId && selectedTaskId !== "new" ? selectedTaskId : undefined
    )
    const newTaskRef = useRef(null)

    if (selectedTaskId === "new" && !newTaskRef.current) {
        newTaskRef.current = {
            _id: "__new__",
            heading: "New Component",
            categoryId: category?.id,
            classification: "NotSpecified",
            frequency: { period: "Year", interval: 1, code: "1Y" },
            skillSet: "Competent Person",
            timing: null,
            taskType: "maintenance",
            scheduleReferences: [],
            hyperlinksToAdd: [],
            warnings: [],
            actionsHtml: "",
            notesHtml: "",
            authorsNotesHtml: "",
            componentInstructionsHtml: "",
        }
    }
    if (selectedTaskId === "new" && newTaskRef.current && category?.id) {
        newTaskRef.current.categoryId = category.id
    }
    if (selectedTaskId !== "new") {
        newTaskRef.current = null
    }

    if (selectedTaskId && selectedTaskId !== "new") {
        if (task === undefined) return <Typography sx={{ p: 3 }}>Loading Component...</Typography>
        if (!task) return <Typography sx={{ p: 3 }}>Component not found: {selectedTaskId}</Typography>
    }

    const initialTask = selectedTaskId === "new" ? newTaskRef.current : task

    return (
        <>
            <SaveIndicator id={SAVE_INDICATOR_ID} slot={AuthoringHeaderBarRight} />
            <EditRecord target={initialTask} store={saveTask} clone={true} id={SAVE_INDICATOR_ID}>
                <TaskEditorContent />
            </EditRecord>
        </>
    )

    async function saveTask(data) {
        const isNew = data._id === "__new__"
        const taskToSave = isNew ? omitTemporaryId(data) : data
        const result = await putMaintenanceTask(taskToSave)
        if (isNew) {
            data._id = result.id
            setSelectedTaskId(result.id)
        }
        parentRefresh()
    }
}

function omitTemporaryId({ _id: __new, ...rest }) {
    return rest
}

function TaskEditorContent() {
    const { target } = useBoundContext()

    return (
        <Bound task={target} editMode={true}>
            <Stack
                spacing={0}
                sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    minWidth: 0,
                    flex: 1,
                    mb: 3,
                }}
            >
                <Stack spacing={2.5} sx={{ p: 3 }}>
                    <BoundTextField field="heading" label="Heading" />

                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <BoundAutocomplete
                            field="classification"
                            label="Classification"
                            options={VALID_CLASSIFICATIONS}
                            size="small"
                            fullWidth={false}
                            sx={{ width: 110, m: 0, p: 0 }}
                        />
                        <Frequency py={0} ml={0} minHeight={0} />
                        <BoundAutocomplete
                            field="skillSet"
                            label="Skill Set"
                            options={SKILL_SETS}
                            freeSolo
                            size="small"
                            fullWidth={false}
                            sx={{ width: 220, m: 0, p: 0 }}
                        />
                        <TimingMinutesEditor />
                    </Stack>
                </Stack>

                <Divider />

                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Actions
                    </Typography>
                    <AuthoringBoundTextEditor field="actionsHtml" noLabel fullHeight />
                </Box>

                <Divider />

                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Notes
                    </Typography>
                    <AuthoringBoundTextEditor field="notesHtml" noLabel fullHeight />
                </Box>

                <Divider />

                <Box sx={{ p: 3 }}>
                    <WarningsEditor />
                </Box>

                <Divider />

                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Author's Notes
                    </Typography>
                    <AuthoringBoundTextEditor field="authorsNotesHtml" noLabel fullHeight />
                </Box>

                <Divider />

                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Component Instructions
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        Internal instructions for authors only. These notes are never included in customer-facing output
                        such as published schedules or PDF exports.
                    </Typography>
                    <AuthoringBoundTextEditor field="componentInstructionsHtml" noLabel fullHeight />
                </Box>

                <Divider />

                <Box sx={{ px: 1 }}>
                    <ScheduleReferencesEditor />
                </Box>

                <Divider />

                <Box sx={{ px: 1 }}>
                    <HyperlinksEditor />
                </Box>

                <Divider />

                <Box sx={{ px: 1 }}>
                    <AdditionalDetailsEditor />
                </Box>
            </Stack>
        </Bound>
    )
}

function TimingMinutesEditor() {
    const [timing, setTiming] = useBoundValue("timing")
    const refresh = useRefresh()

    return (
        <TextField
            label="Timing"
            type="number"
            size="small"
            value={timing?.minutes || ""}
            onChange={handleMinutesChange}
            sx={{ width: 100 }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <Typography variant="caption">min</Typography>
                    </InputAdornment>
                ),
            }}
        />
    )

    function handleMinutesChange(e) {
        const value = e.target.value ? parseFloat(e.target.value) : null
        setTiming(value ? { ...timing, minutes: value } : timing?.note ? { note: timing.note } : null)
        refresh()
    }
}

function AdditionalDetailsEditor() {
    const [timing, setTiming] = useBoundValue("timing")
    const refresh = useRefresh()

    return (
        <Accordion>
            <AccordionSummary
                expandIcon={<Iconify icon="eva:chevron-down-fill" />}
                data-cy="task-editor-additional-accordion-summary"
            >
                <Typography variant="subtitle2">Additional</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    <TextField
                        label="Timing Note"
                        size="small"
                        fullWidth
                        value={timing?.note || ""}
                        onChange={handleNoteChange}
                        placeholder="e.g., total is 5 min when combined"
                    />
                    <BoundAutocomplete
                        field="taskType"
                        label="Component Type"
                        options={TASK_TYPES}
                        size="small"
                        fullWidth={false}
                        sx={{ width: 200, m: 0, p: 0 }}
                    />
                </Stack>
            </AccordionDetails>
        </Accordion>
    )

    function handleNoteChange(e) {
        const { value } = e.target
        setTiming(value ? { ...timing, note: value } : timing?.minutes ? { minutes: timing.minutes } : null)
        refresh()
    }
}

function WarningsEditor() {
    const [warnings = [], setWarnings] = useBoundValue("warnings")
    const [newWarning, setNewWarning] = useState("")
    const [isAdding, setIsAdding] = useState(false)
    const refresh = useRefresh()

    if (!warnings?.length && !isAdding) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Warnings:
                </Typography>
                <Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={showInput}>
                    Add Warning
                </Button>
            </Stack>
        )
    }

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Warnings
            </Typography>
            <Stack spacing={1}>
                <Repeat list={warnings} item={<WarningItem onRemove={removeWarning} />} />
                <Stack direction="row" spacing={1}>
                    <TextField
                        size="small"
                        fullWidth
                        value={newWarning}
                        onChange={(e) => setNewWarning(e.target.value)}
                        placeholder="Add a safety warning..."
                        onKeyDown={handleKeyDown}
                    />
                    <Button variant="outlined" size="small" onClick={addWarning} disabled={!newWarning.trim()}>
                        Add
                    </Button>
                </Stack>
            </Stack>
        </Box>
    )

    function showInput() {
        setIsAdding(true)
    }

    function addWarning() {
        if (newWarning.trim()) {
            setWarnings([...warnings, newWarning.trim()])
            setNewWarning("")
            refresh()
        }
    }

    function removeWarning(index) {
        setWarnings(warnings.filter((_, i) => i !== index))
        refresh()
    }

    function handleKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault()
            addWarning()
        }
    }
}

function WarningItem({ item, index, onRemove }) {
    return (
        <Alert
            severity="warning"
            sx={{ py: 0.5 }}
            action={
                <IconButton aria-label="Remove warning" size="small" onClick={() => onRemove(index)}>
                    <Iconify icon="eva:close-fill" />
                </IconButton>
            }
        >
            {item}
        </Alert>
    )
}

function ScheduleReferencesEditor() {
    const [refs = [], setRefs] = useBoundValue("scheduleReferences")
    const [newCode, setNewCode] = useState("")
    const [newTitle, setNewTitle] = useState("")
    const hasRefs = refs.length > 0
    const { toggle: expanded, onToggle, setToggle: setExpanded } = useToggle(hasRefs)
    const refresh = useRefresh()

    useEffect(expandWhenPopulated, [hasRefs, setExpanded])

    return (
        <Accordion expanded={expanded} onChange={onToggle}>
            <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                <Typography variant="subtitle2">
                    Schedule References {refs?.length > 0 && `(${refs.length})`}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <If value={refs?.length > 0}>
                        <Repeat list={refs} item={<ScheduleRefItem onRemove={removeRef} />} />
                    </If>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value)}
                            placeholder="Code (e.g., 43-09)"
                            sx={{ width: 120 }}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            placeholder="Title (e.g., Power Generation - Standby Generator)"
                            onKeyDown={handleKeyDown}
                        />
                        <Button variant="outlined" size="small" onClick={addRef} disabled={!newCode.trim()}>
                            Add
                        </Button>
                    </Stack>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )

    function expandWhenPopulated() {
        if (hasRefs) setExpanded(true)
    }

    function addRef() {
        if (newCode.trim()) {
            setRefs([...refs, { code: newCode.trim(), title: newTitle.trim() || undefined }])
            setNewCode("")
            setNewTitle("")
            refresh()
        }
    }

    function removeRef(index) {
        setRefs(refs.filter((_, i) => i !== index))
        refresh()
    }

    function handleKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault()
            addRef()
        }
    }
}

function ScheduleRefItem({ item, index, onRemove }) {
    return (
        <Chip
            label={item.title ? `${item.code} - ${item.title}` : item.code}
            onDelete={() => onRemove(index)}
            size="small"
            sx={{ mr: 1, mb: 1 }}
        />
    )
}

function HyperlinksEditor() {
    const [links = [], setLinks] = useBoundValue("hyperlinksToAdd")
    const [newLink, setNewLink] = useState("")
    const hasLinks = links.length > 0
    const { toggle: expanded, onToggle, setToggle: setExpanded } = useToggle(hasLinks)
    const refresh = useRefresh()

    useEffect(expandWhenPopulated, [hasLinks, setExpanded])

    return (
        <Accordion expanded={expanded} onChange={onToggle}>
            <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                <Typography variant="subtitle2">
                    Hyperlinks to Add {links?.length > 0 && `(${links.length})`}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <If value={links?.length > 0}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            <Repeat list={links} item={<HyperlinkItem onRemove={removeLink} />} />
                        </Box>
                    </If>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            fullWidth
                            value={newLink}
                            onChange={(e) => setNewLink(e.target.value)}
                            placeholder="Add reference (e.g., BS 7671:2018, Health and Safety at Work Act 1974)"
                            onKeyDown={handleKeyDown}
                        />
                        <Button variant="outlined" size="small" onClick={addLink} disabled={!newLink.trim()}>
                            Add
                        </Button>
                    </Stack>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )

    function expandWhenPopulated() {
        if (hasLinks) setExpanded(true)
    }

    function addLink() {
        if (newLink.trim()) {
            setLinks([...links, newLink.trim()])
            setNewLink("")
            refresh()
        }
    }

    function removeLink(index) {
        setLinks(links.filter((_, i) => i !== index))
        refresh()
    }

    function handleKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault()
            addLink()
        }
    }
}

function HyperlinkItem({ item, index, onRemove }) {
    return <Chip label={item} onDelete={() => onRemove(index)} size="small" />
}
